import { CalendarWidgetConfig } from '../config'

export interface CalendarSkeletonProps {
  /** Mirrors CalendarGrid's `months-per-view-*` class so the month columns get
   *  the same flex-basis — day cells are square, so their WIDTH sets the
   *  skeleton's height. */
  monthsPerView?: CalendarWidgetConfig['monthsPerView']
  /** The property carries a weekly/monthly discount, so the real grid will
   *  render `.calendar-header-info` above the months. */
  reserveHeader?: boolean
  /** `options.showFooterInfo` — the taxes note + clear button + 3-line legend. */
  reserveFooter?: boolean
  /** `options.showLocalTime` — rendered by an effect a tick after mount. */
  reserveLocalTime?: boolean
}

/**
 * Loading state for the availability grid, which is fetched client-side (#1229).
 *
 * It reuses the REAL container/month/footer class names on purpose: the widget's
 * own CSS then gives it the real paddings, gaps and — because `.slider-track >
 * .month` is `flex: 0 0 50%` and `.days-grid > div` is `aspect-ratio: 1` — the
 * real cell size and therefore the real height. A free-form skeleton measured
 * 246px against the grid's 539px and shifted the page by 0.28 CLS when the fetch
 * landed; mirroring the structure holds the space instead.
 *
 * The pulse heights are not decorative: each one matches the line box of the
 * real content it stands in for (measured against the rendered grid), so the
 * reservation lands within a few pixels at every viewport.
 *
 * Two months is exactly right regardless of how many the grid ends up rendering:
 * the track is `flex-wrap: nowrap` with a fixed 50% (or 100%) basis per month
 * and `.months-container` clips the overflow, so months 3..13 are off-screen and
 * contribute no height.
 */
export default function CalendarSkeleton({
  monthsPerView = 'auto',
  reserveHeader = false,
  reserveFooter = true,
  reserveLocalTime = false,
}: CalendarSkeletonProps = {}) {
  return (
    <div
      className="custom-calendar-container homelocal-availability-calendar-container homelocal-availability-calendar-loader"
      aria-busy="true"
    >
      {reserveHeader && (
        <div className="calendar-header-info">
          <div className="discount-text-container">
            <span className="skeleton-pulse" style={{ display: 'block', width: '260px', height: '24px', borderRadius: '4px' }} />
          </div>
        </div>
      )}

      <div className="calendar-body">
        <div className={`months-container${monthsPerView !== 'auto' ? ` months-per-view-${monthsPerView}` : ''}`}>
          <div className="slider-track">
            <SkeletonMonth />
            <SkeletonMonth />
          </div>
        </div>
      </div>

      {reserveFooter && (
        <div className="calendar-footer-info">
          <div className="footer-instructions">
            {/* Taller than a text line on purpose: the real instructions row is sized by
                the Clear Dates button next to the taxes note, not by the note itself. */}
            <span className="skeleton-pulse" style={{ display: 'block', width: '180px', height: '38px', borderRadius: '4px' }} />
          </div>
          <div className="footer-legend">
            {[0, 1, 2].map((i) => (
              <div key={i} className="footer-legend-item">
                <span className="footer-legend-swatch" />
                <span className="skeleton-pulse" style={{ display: 'block', maxWidth: '220px', width: '100%', height: '20px', borderRadius: '4px' }} />
              </div>
            ))}
          </div>
        </div>
      )}

      {reserveLocalTime && (
        <div className="local-time">
          <span className="skeleton-pulse" style={{ display: 'block', width: '150px', height: '20px', borderRadius: '4px' }} />
        </div>
      )}
    </div>
  )
}

// 42 cells, same as CalendarMonth's fixed TOTAL_CELLS — the real grid always
// renders 6 rows whatever the month, so the reservation never over- or
// under-shoots by a row.
const CELLS = Array.from({ length: 42 }, (_, i) => i)
const WEEKDAYS = Array.from({ length: 7 }, (_, i) => i)

function SkeletonMonth() {
  return (
    <div className="month">
      <div className="calendar-heading month-header">
        <span className="skeleton-pulse" style={{ display: 'inline-block', width: '140px', height: '26px', borderRadius: '4px' }} />
      </div>
      <div className="calendar-table">
        <div className="week-days weekdays">
          {WEEKDAYS.map((i) => (
            <div key={i} className="day-name weekday">
              <span className="skeleton-pulse" style={{ display: 'block', width: '60%', height: '14px', margin: '0 auto', borderRadius: '2px' }} />
            </div>
          ))}
        </div>
        <div className="days-grid">
          {CELLS.map((i) => (
            <div key={i} className="day">
              <span className="skeleton-pulse" style={{ display: 'block', width: '100%', height: '100%', borderRadius: '2px' }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
